import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Edit2, CheckCircle, XCircle, Plus, Building2,
    Calendar, DollarSign, FileText, Clock, AlertTriangle
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Badge, Modal, TextArea,
    ConfirmDialog, toast, Tabs
} from '../../components/common';
import { contractApi } from '../../services/api';
import AmendmentModal from './AmendmentModal';
import './ContractDetail.css';

export default function ContractDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [actionType, setActionType] = useState('');
    const [terminateReason, setTerminateReason] = useState('');
    const [showAmendmentModal, setShowAmendmentModal] = useState(false);
    const [completeMilestone, setCompleteMilestone] = useState(null);

    const { data: contract, isLoading } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => contractApi.getById(id),
        select: (res) => res.data?.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ action, data }) => {
            switch (action) {
                case 'approve': return contractApi.approve(id);
                case 'activate': return contractApi.activate(id);
                case 'terminate': return contractApi.terminate(id, data);
                case 'completeMilestone': return contractApi.completeMilestone(id, data.milestoneId);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const messages = {
                approve: 'approved',
                activate: 'activated',
                terminate: 'terminated',
                completeMilestone: 'Milestone completed'
            };
            toast.success(messages[action] || 'Action completed successfully');
            queryClient.invalidateQueries(['contract', id]);
            setActionType('');
            setTerminateReason('');
            setCompleteMilestone(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'pending_approval': 'warning',
            'approved': 'info',
            'active': 'success',
            'expired': 'default',
            'terminated': 'danger'
        };
        return <Badge variant={config[status] || 'default'} size="lg">{status?.replace('_', ' ')}</Badge>;
    };

    if (isLoading) {
        return <div className="loading-state">Loading...</div>;
    }

    if (!contract) {
        return <div className="error-state">Contract not found</div>;
    }

    const canApprove = contract.status === 'pending_approval';
    const canActivate = contract.status === 'approved';
    const canTerminate = contract.status === 'active';
    const canAmend = contract.status === 'active';
    const canEdit = contract.status === 'draft';

    const tabs = [
        {
            key: 'details',
            label: 'Details',
            content: (
                <div className="contract-details">
                    <div className="detail-section">
                        <h4>Description</h4>
                        <p>{contract.description || 'No description provided'}</p>
                    </div>

                    {contract.paymentTerms && (
                        <div className="detail-section">
                            <h4>Payment Terms</h4>
                            <p>{contract.paymentTerms}</p>
                        </div>
                    )}

                    {contract.termsAndConditions && (
                        <div className="detail-section">
                            <h4>Terms & Conditions</h4>
                            <p>{contract.termsAndConditions}</p>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'milestones',
            label: `Milestones (${contract.milestones?.length || 0})`,
            content: (
                <div className="contract-milestones">
                    {!contract.milestones?.length ? (
                        <div className="empty-state">No milestones defined</div>
                    ) : (
                        <div className="milestones-timeline">
                            {contract.milestones.map((milestone) => (
                                <div key={milestone.id} className={`milestone-card ${milestone.status}`}>
                                    <div className="milestone-status">
                                        {milestone.status === 'completed' ? (
                                            <CheckCircle size={20} />
                                        ) : milestone.status === 'overdue' ? (
                                            <AlertTriangle size={20} />
                                        ) : (
                                            <Clock size={20} />
                                        )}
                                    </div>
                                    <div className="milestone-content">
                                        <h5>{milestone.title}</h5>
                                        <p className="milestone-due">Due: {formatDate(milestone.dueDate)}</p>
                                        <p className="milestone-amount">{formatCurrency(milestone.amount)}</p>
                                    </div>
                                    {milestone.status === 'pending' && canTerminate && (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => setCompleteMilestone(milestone)}
                                        >
                                            Complete
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'amendments',
            label: `Amendments (${contract.amendments?.length || 0})`,
            content: (
                <div className="contract-amendments">
                    {!contract.amendments?.length ? (
                        <div className="empty-state">No amendments</div>
                    ) : (
                        <div className="amendments-list">
                            {contract.amendments.map((amendment) => (
                                <div key={amendment.id} className="amendment-card">
                                    <div className="amendment-header">
                                        <span className="amendment-number">Amendment #{amendment.number}</span>
                                        <Badge variant={amendment.status === 'approved' ? 'success' : 'warning'}>
                                            {amendment.status}
                                        </Badge>
                                    </div>
                                    <p className="amendment-desc">{amendment.description}</p>
                                    <div className="amendment-changes">
                                        <span>Value Change: {formatCurrency(amendment.valueChange)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="contract-detail-page">
            <PageHeader
                title={contract.contractNumber}
                breadcrumbs={[
                    { label: 'Contracts', path: '/contracts' },
                    { label: contract.contractNumber }
                ]}
                actions={
                    <div className="contract-actions">
                        {canEdit && (
                            <Button variant="secondary" icon={Edit2} onClick={() => navigate(`/contracts/${id}/edit`)}>
                                Edit
                            </Button>
                        )}
                        {canApprove && (
                            <Button icon={CheckCircle} onClick={() => setActionType('approve')}>
                                Approve
                            </Button>
                        )}
                        {canActivate && (
                            <Button icon={CheckCircle} onClick={() => setActionType('activate')}>
                                Activate
                            </Button>
                        )}
                        {canAmend && (
                            <Button variant="secondary" icon={Plus} onClick={() => setShowAmendmentModal(true)}>
                                Add Amendment
                            </Button>
                        )}
                        {canTerminate && (
                            <Button variant="danger" icon={XCircle} onClick={() => setActionType('terminate')}>
                                Terminate
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="contract-detail-content">
                <Card>
                    <CardBody>
                        <div className="contract-header">
                            <div>
                                <h2>{contract.title}</h2>
                                <p className="contract-number">{contract.contractNumber}</p>
                            </div>
                            {getStatusBadge(contract.status)}
                        </div>

                        <div className="contract-meta-grid">
                            <div className="meta-item">
                                <Building2 size={16} />
                                <span className="meta-label">Vendor</span>
                                <span className="meta-value">{contract.vendor?.name}</span>
                            </div>
                            <div className="meta-item">
                                <DollarSign size={16} />
                                <span className="meta-label">Total Value</span>
                                <span className="meta-value">{formatCurrency(contract.totalValue)}</span>
                            </div>
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span className="meta-label">Start Date</span>
                                <span className="meta-value">{formatDate(contract.startDate)}</span>
                            </div>
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span className="meta-label">End Date</span>
                                <span className="meta-value">{formatDate(contract.endDate)}</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <Tabs tabs={tabs} defaultTab="details" />
                </Card>
            </div>

            <ConfirmDialog
                isOpen={actionType === 'approve' || actionType === 'activate'}
                onClose={() => setActionType('')}
                onConfirm={() => actionMutation.mutate({ action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Contract`}
                message={`Are you sure you want to ${actionType} this contract?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />

            <Modal
                isOpen={actionType === 'terminate'}
                onClose={() => { setActionType(''); setTerminateReason(''); }}
                title="Terminate Contract"
                size="sm"
            >
                <div className="terminate-form">
                    <p>Please provide a reason for terminating this contract.</p>
                    <TextArea
                        label="Reason"
                        value={terminateReason}
                        onChange={(e) => setTerminateReason(e.target.value)}
                        rows={3}
                        required
                    />
                    <div className="form-actions">
                        <Button variant="secondary" onClick={() => { setActionType(''); setTerminateReason(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => actionMutation.mutate({ action: 'terminate', data: { reason: terminateReason } })}
                            loading={actionMutation.isPending}
                            disabled={!terminateReason.trim()}
                        >
                            Terminate
                        </Button>
                    </div>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={!!completeMilestone}
                onClose={() => setCompleteMilestone(null)}
                onConfirm={() => actionMutation.mutate({ action: 'completeMilestone', data: { milestoneId: completeMilestone?.id } })}
                title="Complete Milestone"
                message={`Mark "${completeMilestone?.title}" as completed?`}
                confirmText="Complete"
                loading={actionMutation.isPending}
            />

            <AmendmentModal
                contractId={id}
                isOpen={showAmendmentModal}
                onClose={() => setShowAmendmentModal(false)}
                onSuccess={() => {
                    setShowAmendmentModal(false);
                    queryClient.invalidateQueries(['contract', id]);
                }}
            />
        </div>
    );
}
